import { FC, useCallback, useEffect, useMemo, useRef, useState, useContext } from "react";

import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import { ParticipantMark } from "@/features/training/model/Training/TrainingSessionParticipants";
import { AlertToast } from "@/shared/notifications/AlertToast";
import {
	sharedTaskDialogActionsSx,
	sharedTaskDialogContentSx,
	sharedTaskDialogHeaderSx,
	sharedTaskDialogPanelSx,
	sharedTaskDialogPaperSx,
} from "@/styles/sharedTaskDialogStyles";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { CancelActionButton, SaveActionButton } from "@/shared/ui";
import {
	Alert,
	Box,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	LinearProgress,
	MenuItem,
	Paper,
	Select,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
	alpha,
	useMediaQuery,
	useTheme,
} from "@mui/material";

import { useMutateCompleteTrainingSession } from "@/service/TrainingMutations";
import { useFetchStudentFailedReasons } from "@/service/TrainingQueries";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";


/* ──────────────────────────────────────────────────────────────────────────── */
/* Types                                                                        */
/* ──────────────────────────────────────────────────────────────────────────── */

interface CompleteCourseSessionProps {
	handleClose: () => void;
	open: boolean;
	id: number;
	students: {
		Id: number;
		Name: string;
		Mark?: string;
		FailedReasonId?: number;
	}[];
}

type Status = "Pass" | "Fail" | "NotComplete";

interface StudentRow {
	id: number;
	Name: string;
	status: Status;
	FailedReasonId: number | null;
}

const isStatus = (value: unknown): value is Status =>
	value === "Pass" || value === "Fail" || value === "NotComplete";

/* ──────────────────────────────────────────────────────────────────────────── */
/* Component                                                                    */
/* ──────────────────────────────────────────────────────────────────────────── */

export const CompleteCourseSession: FC<CompleteCourseSessionProps> = (
	props,
) => {
	const translation: Language | null = useContext(TranslationContext);
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const { isLoading: isLoadingFailedReasons, data: failedReasonOptions } =
		useFetchStudentFailedReasons();

	const [rows, setRows] = useState<StudentRow[]>([]);
	const [comment, setComment] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [markAllValue, setMarkAllValue] = useState<string>("");
	const [isCompleting, setIsCompleting] = useState(false);
	const tableContainerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		setRows(
			props?.students?.map((student, idx) => ({
				id: student.Id ?? idx,
				Name: student.Name,
				status:
					student.Mark === "Fail" || student.Mark === ParticipantMark.Fail
						? "Fail"
						: student.Mark === "NotComplete" ||
							  student.Mark === ParticipantMark.NotComplete
							? "NotComplete"
							: "Pass",
				FailedReasonId: student.FailedReasonId ?? null,
			})),
		);
	}, [props.students]);

	const completeTrainingSession = useMutateCompleteTrainingSession();

	const passCount = useMemo(
		() => rows.filter((r) => r.status === "Pass").length,
		[rows],
	);
	const failCount = useMemo(
		() => rows.filter((r) => r.status === "Fail").length,
		[rows],
	);
	const pendingCount = useMemo(
		() => rows.filter((r) => r.status === "NotComplete").length,
		[rows],
	);
	const hasInvalidFail = useMemo(
		() => rows.some((r) => r.status === "Fail" && !r.FailedReasonId),
		[rows],
	);

	const handleMarkAllAs = useCallback((status: Status) => {
		setRows((prev) =>
			prev.map((row) => ({
				...row,
				status,
				FailedReasonId: status === "Fail" ? row.FailedReasonId : null,
			})),
		);
		setError(null);
	}, []);

	const handleStatusChange = useCallback((id: number, value: Status) => {
		setRows((prev) =>
			prev.map((row) =>
				row.id === id
					? {
							...row,
							status: value,
							FailedReasonId: value === "Fail" ? row.FailedReasonId : null,
						}
					: row,
			),
		);
		setError(null);
	}, []);

	const handleFailedReasonChange = useCallback(
		(id: number, value: number | "") => {
			setRows((prev) =>
				prev.map((row) =>
					row.id === id
						? { ...row, FailedReasonId: value === "" ? null : Number(value) }
						: row,
				),
			);
			setError(null);
		},
		[],
	);

	// Keyboard: ArrowUp/Down for row nav, Enter toggles Pass/Fail
	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent, rowIndex: number) => {
			if (e.key === "ArrowDown" && rowIndex < rows.length - 1) {
				e.preventDefault();
				const nextRow = tableContainerRef.current?.querySelector(
					`[data-row-index="${rowIndex + 1}"]`,
				) as HTMLElement;
				nextRow?.focus();
			}
			if (e.key === "ArrowUp" && rowIndex > 0) {
				e.preventDefault();
				const prevRow = tableContainerRef.current?.querySelector(
					`[data-row-index="${rowIndex - 1}"]`,
				) as HTMLElement;
				prevRow?.focus();
			}
			if (e.key === "Enter") {
				e.preventDefault();
				const row = rows[rowIndex];
				if (row) {
					const next = row.status === "Pass" ? "Fail" : "Pass";
					handleStatusChange(row.id, next);
				}
			}
		},
		[rows, handleStatusChange],
	);

	const handleComplete = async () => {
		if (isCompleting) {
			return;
		}

		setError(null);

		if (pendingCount > 0) {
			setError("All students must be marked as either Pass or Fail.");
			return;
		}
		if (hasInvalidFail) {
			setError("A reason must be selected for each student marked as Fail.");
			return;
		}

		const studentData = rows.map((row) => ({
			Id: row.id,
			Name: row.Name,
			Mark: row.status as ParticipantMark,
			FailedReasonId: row.FailedReasonId ?? 0,
		}));

		try {
			setIsCompleting(true);
			await completeTrainingSession({
				sessionId: props.id,
				request: { Students: studentData, Comment: comment },
			});
			await AlertToast.success("Session completion saved");
			props.handleClose();
		} catch (err) {
			console.error(err);
			await AlertToast.failed("Failed to save session completion");
		} finally {
			setIsCompleting(false);
		}
	};

	if (isLoadingFailedReasons)
		return (
			<Box
				sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
			>
				<LinearProgress />
			</Box>
		);

	const saveDisabled = pendingCount > 0 || hasInvalidFail || isCompleting;
	const closeButtonSx = {
		color: theme.palette.text.secondary,
		"&:hover": {
			color: theme.palette.text.primary,
			backgroundColor: theme.palette.action.hover,
		},
	};

	return (
		<Dialog
			onClose={isCompleting ? undefined : props.handleClose}
			open={props.open}
			fullWidth
			maxWidth="md"
			fullScreen={isMobile}
			aria-labelledby="complete-training-session-title"
			PaperProps={{
				sx: sharedTaskDialogPaperSx(theme, isMobile),
			}}
		>
			<DialogTitle
				id="complete-training-session-title"
				component="div"
				sx={{
					...sharedTaskDialogHeaderSx(theme),
					position: "relative",
					px: { xs: 2, sm: 2.5 },
					pt: 2,
					pb: 1.5,
				}}
			>
				<Typography
					variant="caption"
					sx={{
						display: "block",
						fontWeight: 800,
						letterSpacing: "0.08em",
						color: "primary.main",
						mb: 0.4,
					}}
				>
					<Translate String="Auto.Features.Training.Pages.TrainingTab.PanelRight.TrainingSession.CompleteCourseSession.L289C6.TRAINING">TRAINING</Translate>
				</Typography>
				<Typography
					variant="h6"
					component="h2"
					sx={{ fontWeight: 700, color: "text.primary", pr: 5, mb: 0.75 }}
				>
					<Translate String="Training.CompleteTrainingSession.Title">
						Complete Training Session
					</Translate>
				</Typography>
				<Typography
					variant="body2"
					color="text.secondary"
					sx={{ mt: 0.5, display: "block", pr: 5 }}
				>
					<Translate String="Training.CompleteTrainingSession.Subtitle">
						Review participants, mark results, and save a final session comment.
					</Translate>
				</Typography>
				<IconButton
					aria-label={GetLanguageValueAsString(translation, "Auto.Features.Training.Pages.TrainingTab.PanelRight.TrainingSession.CompleteCourseSession.L311C17.Close", "Close")}
					onClick={props.handleClose}
					size="small"
					disabled={isCompleting}
					sx={{ position: "absolute", right: 8, top: 8, ...closeButtonSx }}
				>
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>

			<DialogContent
				sx={{
					...sharedTaskDialogContentSx(theme),
					px: { xs: 2, sm: 2.5 },
					pt: 2,
					pb: 0,
					display: "flex",
					flexDirection: "column",
					minHeight: 0,
					flex: 1,
					overflow: "auto",
				}}
			>
				{(() => {
					const sectionCardSx = {
						...sharedTaskDialogPanelSx(theme),
						p: { xs: 2, md: 2.5 },
					};
					return (
						<>
				{error && (
					<Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
						{error}
					</Alert>
				)}

				{/* Summary */}
				<Paper
					variant="outlined"
					sx={{
						mb: 2,
						...sectionCardSx,
						bgcolor: "action.hover",
					}}
				>
					<Stack
						direction="row"
						alignItems="center"
						justifyContent="space-between"
						flexWrap="wrap"
						gap={1}
						>
							<Typography variant="body2" fontWeight={600}>
								{rows.length}{" "}
								<Translate String="Common.Participants">participants</Translate>
								{" · "}{passCount}{" "}
								<Translate String="Common.Pass">pass</Translate>
								{" · "}{failCount}{" "}
								<Translate String="Common.Fail">fail</Translate>
								{pendingCount > 0 && (
									<>
										{" · "}
										{pendingCount}{" "}
										<Translate String="Common.Pending">pending</Translate>
									</>
								)}
							</Typography>
							<Stack direction="row" alignItems="center" gap={1}>
								<Typography variant="caption" color="text.secondary">
									<Translate String="Training.CompleteTrainingSession.MarkAllAs">
										Mark all as:
									</Translate>
								</Typography>
							<Select
								size="small"
								value={markAllValue}
								displayEmpty
								onChange={(e) => {
									const v = e.target.value as Status;
									if (v) {
										handleMarkAllAs(v);
										setMarkAllValue("");
									}
								}}
								sx={{
									minWidth: 100,
									bgcolor: "background.paper",
									borderRadius: 2,
									fontWeight: 600,
									"& .MuiSelect-select": { py: 0.5, px: 1.25 },
								}}
								>
									<MenuItem value="">
										<em>
											<Translate String="Common.Select">Select...</Translate>
										</em>
									</MenuItem>
									<MenuItem value="Pass">
										<Translate String="Common.Pass">Pass</Translate>
									</MenuItem>
									<MenuItem value="Fail">
										<Translate String="Common.Fail">Fail</Translate>
									</MenuItem>
								</Select>
						</Stack>
					</Stack>
				</Paper>

				{/* Participants table */}
				{rows.length > 0 ? (
					<Paper variant="outlined" sx={{ ...sectionCardSx, mb: 2 }}>
						<Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
							<Translate String="Training.CompleteTrainingSession.Participants">
								Participants
							</Translate>
						</Typography>
						<TableContainer
							ref={tableContainerRef}
							component={Paper}
							variant="outlined"
							sx={{
								borderRadius: 2,
								maxHeight: 340,
								overflow: "auto",
								"& .MuiTableHead-root": {
									position: "sticky",
									top: 0,
									zIndex: 1,
									bgcolor: "background.default",
									"& th": {
										bgcolor: "background.default",
										fontWeight: 700,
										py: 1.25,
										px: 1.5,
										borderBottom: 1,
										borderColor: "divider",
									},
								},
								"& .MuiTableRow-root:nth-of-type(even)": {
									bgcolor: "action.hover",
								},
								"& .MuiTableRow-root:hover": {
									bgcolor: "action.selected",
								},
							}}
						>
								<Table
									size="small"
									stickyHeader
									aria-label={GetLanguageValueAsString(
										translation,
										"Training.CompleteTrainingSession.Table",
										"session completion table",
									)}
								>
								<TableHead>
									<TableRow>
										<TableCell sx={{ width: "45%" }}>
											<Translate String="Common.Name">Name</Translate>
										</TableCell>
										<TableCell sx={{ width: "55%", px: 1 }}>
											<Translate String="Training.CompleteTrainingSession.Status">
												Status
											</Translate>
										</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{rows.map((row, idx) => (
										<TableRow
											key={row.id}
											data-row-index={idx}
											tabIndex={0}
											onKeyDown={(e) => handleKeyDown(e, idx)}
											sx={{
												"& td": { py: 0.75, px: 1.5, verticalAlign: "middle" },
											}}
										>
											<TableCell sx={{ fontWeight: 500 }}>{row.Name}</TableCell>
											<TableCell>
												<Stack
													direction="row"
													alignItems="center"
													gap={1.25}
													flexWrap="wrap"
												>
													<ToggleButtonGroup
														value={row.status}
														exclusive
														onChange={(_, value: unknown) => {
															if (isStatus(value)) {
																handleStatusChange(row.id, value);
															}
														}}
														size="small"
														sx={{
															borderRadius: 2,
															overflow: "hidden",
															border: "1px solid",
															borderColor: "divider",
															"& .MuiToggleButton-root": {
																py: 0.375,
																px: 1.5,
																textTransform: "none",
																fontWeight: 600,
																border: "none",
																"&.pass-btn.Mui-selected": {
																	bgcolor: (t) =>
																		alpha(t.palette.success.main, 0.12),
																	color: "success.main",
																	"&:hover": {
																		bgcolor: (t) =>
																			alpha(t.palette.success.main, 0.2),
																	},
																},
																"&.fail-btn.Mui-selected": {
																	bgcolor: (t) =>
																		alpha(t.palette.error.main, 0.12),
																	color: "error.main",
																	"&:hover": {
																		bgcolor: (t) =>
																			alpha(t.palette.error.main, 0.2),
																	},
																},
															},
														}}
													>
															<ToggleButton
																value="Pass"
																className="pass-btn"
																aria-label={GetLanguageValueAsString(
																	translation,
																	"Common.Pass",
																	"Pass",
																)}
															>
															<CheckCircleOutlineIcon
																sx={{ fontSize: 18, mr: 0.5 }}
															/>
																<Translate String="Common.Pass">Pass</Translate>
															</ToggleButton>
															<ToggleButton
																value="Fail"
																className="fail-btn"
																aria-label={GetLanguageValueAsString(
																	translation,
																	"Common.Fail",
																	"Fail",
																)}
															>
															<HighlightOffIcon
																sx={{ fontSize: 18, mr: 0.5 }}
															/>
																<Translate String="Common.Fail">Fail</Translate>
															</ToggleButton>
													</ToggleButtonGroup>
													{row.status === "Fail" && (
														<Select
															size="small"
															value={row.FailedReasonId ?? ""}
															onChange={(e) =>
																handleFailedReasonChange(
																	row.id,
																	e.target.value as number | "",
																)
															}
															displayEmpty
															sx={{
																minWidth: 140,
																bgcolor: "background.paper",
																borderRadius: 2,
																fontWeight: 500,
																"& .MuiSelect-select": { py: 0.5, px: 1.25 },
																"& .MuiOutlinedInput-notchedOutline": {
																	borderColor: "divider",
																},
															}}
															>
																<MenuItem value="">
																	<em>
																		<Translate String="Training.CompleteTrainingSession.SelectReason">
																			Select reason...
																		</Translate>
																	</em>
																</MenuItem>
															{(failedReasonOptions ?? []).map((opt) => (
																<MenuItem key={opt.Id} value={opt.Id}>
																	{opt.Name}
																</MenuItem>
															))}
														</Select>
													)}
													{row.status === "Fail" && !row.FailedReasonId && (
														<Typography
															variant="caption"
															color="error.main"
															fontWeight={500}
															>
																<Translate String="Common.Required">
																	Required
																</Translate>
															</Typography>
														)}
												</Stack>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					</Paper>
					) : (
						<Box sx={{ py: 3, textAlign: "center" }}>
							<Typography color="text.secondary">
								<Translate String="Training.CompleteTrainingSession.NoParticipants">
									No participants.
								</Translate>
							</Typography>
						</Box>
					)}

				{/* General Comment */}
				<Paper variant="outlined" sx={{ ...sectionCardSx, mb: 2 }}>
					<Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
						<Translate String="Training.CompleteTrainingSession.GeneralComment">
							General Comment
						</Translate>
					</Typography>
					<TextField
						label=""
						placeholder={GetLanguageValueAsString(
							translation,
							"Training.CompleteTrainingSession.CommentPlaceholder",
							"Notes about this session (optional)",
						)}
						variant="outlined"
						fullWidth
						value={comment}
						onChange={(e) => setComment(e.target.value)}
						sx={{
							bgcolor: "background.paper",
							borderRadius: 1,
							"& .MuiOutlinedInput-root": { borderRadius: 1 },
						}}
						multiline
						rows={3}
					/>
				</Paper>
						</>
					);
				})()}
			</DialogContent>

			<DialogActions
				sx={{
					...sharedTaskDialogActionsSx(theme, isMobile),
					px: { xs: 2, sm: 2.5 },
					pt: 2,
					display: "flex",
					justifyContent: "flex-end",
					gap: 1.5,
					flexShrink: 0,
					position: "sticky",
					bottom: 0,
					zIndex: 1,
				}}
			>
				<CancelActionButton
					onClick={props.handleClose}
					disabled={isCompleting}
				>
					<Translate String="Procedure.Button.Cancel">Cancel</Translate>
				</CancelActionButton>
				<SaveActionButton
					onClick={() => {
						void handleComplete();
					}}
					loading={isCompleting}
					loadingText={
						<Translate String="LabDaily.Saving">Saving...</Translate>
					}
					disabled={saveDisabled}
				>
					<Translate String="Training.CompleteTrainingSession.SaveResults">
						Save Results
					</Translate>
				</SaveActionButton>
			</DialogActions>
		</Dialog>
	);
};
